/**
 * DNA Fingerprint Image Generator
 *
 * Creates a miniature visual fingerprint of a PDF template by drawing horizontal
 * lines representing the position and width of text field annotations on page 1.
 * The resulting image enables quick visual comparison and MSSIM-based template
 * matching without inspecting the full PDF.
 *
 * Output: always 85×110 portrait PNG. Landscape pages are rotated 90° CCW (top edge
 * becomes left edge) and fields are drawn as vertical lines, so all fingerprints
 * share the same dimensions for direct MSSIM comparison.
 */

/** Canvas dimensions — always portrait */
const IMAGE_WIDTH = 85
const IMAGE_HEIGHT = 110

/** Minimum field extent in PDF points to include (along the field's wider axis) */
const MIN_FIELD_EXTENT_PT = 50

/**
 * Generate a DNA fingerprint image from a pdfjs document.
 * Returns a PNG data URL, or null if generation fails.
 */
export async function generateDnaImage(pdfDoc: any): Promise<string | null> {
  try {
    const page = await pdfDoc.getPage(1)
    const viewport = page.getViewport({ scale: 1.0 })
    const pageW = viewport.width
    const pageH = viewport.height
    const landscape = pageW > pageH

    // Effective dimensions after rotation (always portrait)
    const effW = landscape ? pageH : pageW
    const effH = landscape ? pageW : pageH
    const scaleX = IMAGE_WIDTH / effW
    const scaleY = IMAGE_HEIGHT / effH

    // Collect field rects from annotations
    const rects: number[][] = []
    const annotations = await page.getAnnotations()
    for (const annot of annotations) {
      if (annot.subtype !== 'Widget') continue
      const ft = annot.fieldType
      if (ft === 'Btn' || ft === 'Ch') continue
      if (annot.rect?.length >= 4) rects.push(annot.rect)
    }

    // Supplement from AcroForm field objects
    const acroForm = await pdfDoc.getFieldObjects()
    if (acroForm) {
      const seen = new Set(rects.map((r) => r.join(',')))
      for (const [, fieldData] of Object.entries(acroForm)) {
        const fieldArray = Array.isArray(fieldData) ? fieldData : [fieldData]
        for (const fieldObj of fieldArray) {
          const fo = fieldObj as any
          if (fo.page !== 0) continue
          if (!fo.rect || fo.rect.length < 4) continue
          if (fo.type === 'Btn' || fo.type === 'Ch') continue
          const key = fo.rect.join(',')
          if (!seen.has(key)) {
            rects.push(fo.rect)
            seen.add(key)
          }
        }
      }
    }

    // Map each rect to a canvas strand
    // Portrait: horizontal line (x, y, length along x)
    // Landscape (rotated 90° CCW): vertical line (x, y, length along y)
    //   CCW rotation: (x, y) → (pageH - y, x) — top edge becomes left edge
    const strands: { x: number; y: number; len: number; vertical: boolean }[] = []
    const strandKeys = new Set<string>()

    for (const rect of rects) {
      const [x1, y1, x2, y2] = rect
      let sx: number, sy: number, slen: number

      if (landscape) {
        // 90° CCW: (x, y) → (pageH - y, x)
        // Field's x-extent (width) maps to vertical length
        const fieldW = Math.abs(x2 - x1)
        if (fieldW < MIN_FIELD_EXTENT_PT) continue
        const left = pageH - Math.max(y1, y2)
        const top = Math.min(x1, x2)
        sx = Math.round(left * scaleX)
        sy = Math.round(top * scaleY)
        slen = Math.round(fieldW * scaleY)
      }
      else {
        const fieldW = Math.abs(x2 - x1)
        if (fieldW < MIN_FIELD_EXTENT_PT) continue
        const left = Math.min(x1, x2)
        const top = pageH - Math.max(y1, y2)
        sx = Math.round(left * scaleX)
        sy = Math.round(top * scaleY)
        slen = Math.round(fieldW * scaleX)
      }

      const key = `${sx},${sy},${slen}`
      if (!strandKeys.has(key)) {
        strands.push({ x: sx, y: sy, len: slen, vertical: landscape })
        strandKeys.add(key)
      }
    }

    // Draw onto a canvas
    const canvas = document.createElement('canvas')
    canvas.width = IMAGE_WIDTH
    canvas.height = IMAGE_HEIGHT
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, IMAGE_WIDTH, IMAGE_HEIGHT)

    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 1

    for (const { x, y, len, vertical } of strands) {
      ctx.beginPath()
      ctx.moveTo(x, y)
      if (vertical) {
        ctx.lineTo(x, y + len)
      }
      else {
        ctx.lineTo(x + len, y)
      }
      ctx.stroke()
    }

    return canvas.toDataURL('image/png')
  }
  catch (error) {
    console.error('Failed to generate DNA image:', error)
    return null
  }
}
